<template>
  <div>
    <p class="mb-3">
      {{ $t("production_strategy.forward_materials.description") }}
    </p>
    <ForwardSalesMaterialsComponent :country class="mb-4" />

    <h5 class="mb-2 fs-8">
      {{ $t("production_strategy.forward_mandates_list.title") }}
    </h5>

    <LoadingIndicatorComponent v-if="loading" />

    <RetryableErrorNoteComponent
      v-else-if="errorMessage"
      :message="errorMessage"
      @retry="load"
    />

    <template v-else>
      <ul v-if="mandates.length > 0" class="list-unstyled mb-0">
        <li
          v-for="mandate in mandates"
          :key="mandate.mandate_id"
          class="d-flex align-items-center gap-2 py-1"
          data-testid="forward-mandate"
        >
          <span>{{ mandate.name }}</span>
          <span
            v-if="mandate.listing_kind === 'pay_as_produced'"
            class="badge text-bg-light fw-normal"
          >
            {{ $t("forwards.common.status.pay_as_produced") }}
          </span>
        </li>
      </ul>
      <p v-else class="text-body-tertiary fs-9 mb-0">
        {{ $t("production_strategy.forward_mandates_list.empty") }}
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import RetryableErrorNoteComponent from "@/forwards2/components/RetryableErrorNoteComponent.vue";
import {
  forwardsApi,
  forwardsErrorMessage,
  isDomainError,
} from "@/forwards2/forwardsApi";
import type { ActiveMandateRow } from "@/forwards2/forwardsTypes";
import { computed, ref, shallowRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import ForwardSalesMaterialsComponent from "@/forwards2/components/ForwardSalesMaterialsComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

/**
 * The small-producer dashboard's forward sales panel: the forward sales onboarding materials, then
 * the active forward mandates the dashboard's companies are members of, by name. Small producers do not
 * manage mandates themselves (user decision, 2026-10-09), so nothing here opens one. Refetched
 * whenever the dashboard's company selection changes.
 */
const props = withDefaults(
  defineProps<{
    companyIds: number[];
    /** Picks the materials' language version. */
    country?: string | null;
  }>(),
  { country: null }
);

const { globalErrorHandler } = useGlobalHandlers();
const { t } = useI18n();

const mandates = ref<ActiveMandateRow[]>([]);
const loading = ref(false);
// `shallowRef`: keeps a foreign error object out of a reactive proxy.
const error = shallowRef<unknown>(null);

const errorMessage = computed(() => {
  if (error.value === null) return null;
  return (
    forwardsErrorMessage(error.value) ??
    t("production_strategy.forward_mandates_list.load_failed")
  );
});

// A selection change fires a new request while the old one may still be in flight; only the newest
// may write, or a late answer would list another selection's mandates.
let generation = 0;

/** Fetches the mandates of the current companies. Never throws — failures land in `error`. */
const load = async () => {
  const current = ++generation;
  if (props.companyIds.length === 0) {
    mandates.value = [];
    error.value = null;
    loading.value = false;
    return;
  }
  loading.value = true;
  try {
    const response = await forwardsApi.listActiveMandates(props.companyIds);
    if (current !== generation) return;
    mandates.value = response.data;
    error.value = null;
  } catch (caught) {
    if (current !== generation) return;
    mandates.value = [];
    error.value = caught;
    if (!isDomainError(caught)) globalErrorHandler(caught);
  } finally {
    if (current === generation) loading.value = false;
  }
};

watch(() => props.companyIds.join(","), load, { immediate: true });
</script>
